/*
 * Cart drawer.
 *
 * Intercepts add-to-cart submissions, talks to the Cart AJAX API, and refreshes
 * the drawer markup through the Section Rendering API so the template stays in
 * Liquid.
 *
 * Every form keeps its native action, so the cart still works with JavaScript
 * disabled or before this script loads.
 */
(function () {
  "use strict";

  var SECTION_ID = "cart-drawer";
  var OPEN_CLASS = "cart-drawer-open";

  var lastTrigger = null;

  function drawer() {
    return document.querySelector("[data-cart-drawer]");
  }

  function sectionWrapper() {
    return document.getElementById("shopify-section-" + SECTION_ID);
  }

  function announce(message) {
    var region = document.querySelector("[data-cart-status]");
    if (region) region.textContent = message;
  }

  /* ---------- open / close ---------- */

  function focusableIn(element) {
    return Array.prototype.filter.call(
      element.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
      ),
      function (node) {
        return node.offsetParent !== null;
      }
    );
  }

  function syncAriaHidden() {
    var element = drawer();
    if (element) {
      element.setAttribute(
        "aria-hidden",
        document.body.classList.contains(OPEN_CLASS) ? "false" : "true"
      );
    }
  }

  function open(trigger) {
    if (document.body.classList.contains(OPEN_CLASS)) return;

    lastTrigger = trigger || document.activeElement;

    var element = drawer();
    var panel = element && element.querySelector(".cart-drawer__panel");

    // Adding to the cart re-renders the section, so this panel may have been
    // in the document for less than a frame. Force a style computation to give
    // the browser a closed position to start from — without it the class below
    // is the panel's first computed style and the drawer snaps open with no
    // animation.
    if (panel) void panel.offsetWidth;

    document.body.classList.add(OPEN_CLASS);
    syncAriaHidden();

    if (!element) return;

    var closeButton = element.querySelector("[data-cart-close]");
    if (closeButton) closeButton.focus();
  }

  function close() {
    if (!document.body.classList.contains(OPEN_CLASS)) return;

    document.body.classList.remove(OPEN_CLASS);
    syncAriaHidden();

    // Return focus to whatever opened the drawer, but only if it is still in
    // the document — the add-to-cart button survives, a removed line item's
    // button does not.
    if (lastTrigger && document.contains(lastTrigger)) {
      lastTrigger.focus();
    }
    lastTrigger = null;
  }

  function trapFocus(event) {
    if (event.key !== "Tab") return;
    if (!document.body.classList.contains(OPEN_CLASS)) return;

    var element = drawer();
    if (!element) return;

    var focusable = focusableIn(element);
    if (!focusable.length) return;

    var first = focusable[0];
    var last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  /* ---------- rendering ---------- */

  function refresh() {
    return fetch(window.Shopify.routes.root + "?sections=" + SECTION_ID, {
      headers: { "X-Requested-With": "XMLHttpRequest" },
    })
      .then(function (response) {
        if (!response.ok) throw new Error("Could not load the cart");
        return response.json();
      })
      .then(function (sections) {
        var wrapper = sectionWrapper();
        if (!wrapper) return;

        var parsed = new DOMParser().parseFromString(sections[SECTION_ID], "text/html");
        var fresh = parsed.getElementById("shopify-section-" + SECTION_ID);
        wrapper.innerHTML = fresh ? fresh.innerHTML : sections[SECTION_ID];

        syncAriaHidden();
      });
  }

  function updateCount(count) {
    var badge = document.querySelector("[data-cart-count]");
    if (!badge) return;

    badge.textContent = count;
    badge.hidden = count === 0;
  }

  /* ---------- cart API ---------- */

  function cartRoutes() {
    return window.Shopify && window.Shopify.routes ? window.Shopify.routes : { root: "/" };
  }

  // `disabled` alone is invisible on a button with a solid background, so the
  // customer gets no sign the click registered during the request. Swap the
  // label and flag aria-busy, which also drives the spinner in theme.css.
  function setBusy(button, busy) {
    if (!button) return;

    if (busy) {
      button.dataset.idleLabel = button.textContent.trim();
      button.textContent = "Adding…";
      button.setAttribute("aria-busy", "true");
      button.setAttribute("disabled", "disabled");
      return;
    }

    if (button.dataset.idleLabel) {
      button.textContent = button.dataset.idleLabel;
      delete button.dataset.idleLabel;
    }
    button.removeAttribute("aria-busy");
    button.removeAttribute("disabled");
  }

  function addToCart(form) {
    var button = form.querySelector('[name="add"]');
    setBusy(button, true);

    return fetch(cartRoutes().root + "cart/add.js", {
      method: "POST",
      headers: { Accept: "application/json" },
      body: new FormData(form),
    })
      .then(function (response) {
        return response.json().then(function (body) {
          if (!response.ok) throw new Error(body.description || "Could not add to cart");
          return body;
        });
      })
      .then(function (item) {
        announce(item.product_title + " added to your cart");
        return refreshAndCount();
      })
      .catch(function (error) {
        announce(error.message);
        // Fall back to the real form so the customer is never stuck.
        form.submit();
      })
      .finally(function () {
        setBusy(button, false);
      });
  }

  /* ---------- quantity changes ---------- */

  /*
   * Steppers repaint immediately and the request is debounced, so clicking "+"
   * three times reads as +3 and sends one request carrying the final value.
   * Sending per click would instead fire three requests that each carry the
   * quantity rendered before the burst started, and they would race.
   *
   * Pending targets are keyed by line key, which survives the renumbering that
   * removing a line causes.
   */
  var DEBOUNCE_MS = 250;

  var pendingChanges = {};
  var flushTimer = null;
  var flushing = false;

  function quantityOf(key) {
    if (Object.prototype.hasOwnProperty.call(pendingChanges, key)) {
      return pendingChanges[key];
    }
    var element = drawer();
    var node = element && element.querySelector('[data-line-qty="' + key + '"]');
    return node ? Number(node.textContent.trim()) || 0 : 0;
  }

  // Repaint from the pending targets so the drawer reflects the clicks right
  // away; prices and the subtotal catch up when the section re-renders.
  function paintQuantities() {
    var element = drawer();
    if (!element) return;

    Object.keys(pendingChanges).forEach(function (key) {
      var node = element.querySelector('[data-line-qty="' + key + '"]');
      if (node) node.textContent = pendingChanges[key];

      var line = element.querySelector('[data-line-item="' + key + '"]');
      if (line) line.classList.toggle("cart-line--removing", pendingChanges[key] === 0);
    });

    var total = 0;
    Array.prototype.forEach.call(element.querySelectorAll("[data-line-qty]"), function (node) {
      total += Number(node.textContent.trim()) || 0;
    });
    updateCount(total);
  }

  function queueChange(key, quantity) {
    pendingChanges[key] = Math.max(0, quantity);
    paintQuantities();

    if (flushTimer) clearTimeout(flushTimer);
    // Removing is a deliberate one-off, so there is nothing to coalesce.
    flushTimer = setTimeout(flushChanges, quantity === 0 ? 0 : DEBOUNCE_MS);
  }

  function flushChanges() {
    flushTimer = null;

    // A flush is already running; its finally block picks up whatever is
    // queued by then, so dropping out here cannot lose changes.
    if (flushing) return;

    var batch = pendingChanges;
    var keys = Object.keys(batch);
    if (!keys.length) return;

    pendingChanges = {};
    flushing = true;

    var removed = false;

    keys
      .reduce(function (chain, key) {
        return chain.then(function () {
          if (batch[key] === 0) removed = true;

          return fetch(cartRoutes().root + "cart/change.js", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
            },
            body: JSON.stringify({ id: key, quantity: batch[key] }),
          }).then(function (response) {
            if (!response.ok) throw new Error("Could not update the cart");
            return response.json();
          });
        });
      }, Promise.resolve())
      .then(function (cart) {
        announce(removed ? "Item removed" : "Cart updated");
        if (cart) updateCount(cart.item_count);
        return refresh();
      })
      .catch(function (error) {
        announce(error.message);
        // The optimistic paint may be wrong now, so take the server's word.
        return refreshAndCount();
      })
      .finally(function () {
        flushing = false;
        if (Object.keys(pendingChanges).length) flushChanges();
      });
  }

  function refreshAndCount() {
    return fetch(cartRoutes().root + "cart.js", { headers: { Accept: "application/json" } })
      .then(function (response) {
        return response.json();
      })
      .then(function (cart) {
        updateCount(cart.item_count);
        return refresh();
      });
  }

  /* ---------- checkout ---------- */

  /*
   * Checkout is a real form post that navigates to Shopify, so there is no
   * promise to hang a spinner on — the page simply sits there until the
   * browser moves. Flag the button busy on submit and let the navigation run.
   *
   * Deliberately not `disabled`: disabling the submitter inside its own submit
   * handler can drop its name from the payload, and Shopify keys checkout on
   * the presence of `checkout`. Losing it would post a plain cart update
   * instead. Re-clicks are blocked with pointer-events and the flag below.
   */
  var checkingOut = false;

  function handleCheckoutSubmit(event) {
    var submitter = event.submitter || document.activeElement;
    if (!submitter || submitter.name !== "checkout") return;

    if (checkingOut) {
      event.preventDefault();
      return;
    }

    checkingOut = true;
    submitter.dataset.idleLabel = submitter.textContent.trim();
    submitter.textContent = "Loading…";
    submitter.setAttribute("aria-busy", "true");
  }

  // Coming back via the back button can restore the page from the bfcache with
  // the button still mid-checkout, so reset it.
  window.addEventListener("pageshow", function (event) {
    if (!event.persisted) return;

    checkingOut = false;
    Array.prototype.forEach.call(
      document.querySelectorAll('[name="checkout"][aria-busy="true"]'),
      function (button) {
        if (button.dataset.idleLabel) {
          button.textContent = button.dataset.idleLabel;
          delete button.dataset.idleLabel;
        }
        button.removeAttribute("aria-busy");
      }
    );
  });

  /* ---------- wiring ---------- */

  document.addEventListener("click", function (event) {
    var toggle = event.target.closest("[data-cart-toggle]");
    if (toggle) {
      event.preventDefault();
      open(toggle);
      return;
    }

    if (event.target.closest("[data-cart-close]") || event.target.closest("[data-cart-overlay]")) {
      event.preventDefault();
      close();
      return;
    }

    var step = event.target.closest("[data-cart-change]");
    if (step) {
      event.preventDefault();

      var key = step.dataset.key;
      var target =
        step.dataset.delta === undefined
          ? Number(step.dataset.quantity)
          : quantityOf(key) + Number(step.dataset.delta);

      queueChange(key, target);
    }
  });

  document.addEventListener("keydown", function (event) {
    if (event.key === "Escape") close();
    trapFocus(event);
  });

  document.addEventListener("submit", function (event) {
    var form = event.target;
    if (!form.action) return;

    if (form.action.indexOf("/cart/add") === -1) {
      handleCheckoutSubmit(event);
      return;
    }

    event.preventDefault();
    addToCart(form).then(function () {
      open(form.querySelector('[name="add"]'));
    });
  });

  syncAriaHidden();
})();
